export enum OrganizationType {
    RECYCLER = 'recycler',
    TRUCK = 'truck',
    WASTE_OUTLET = 'waste_outlet',
    CHARITY = 'charity',
    DISPOSAL = 'disposal',
}

export type OrganizationStatus = 'active' | 'inactive';

/** Human-readable labels for chips and selects (API values stay snake_case enums). */
export const ORGANIZATION_TYPE_LABELS: Record<OrganizationType, string> = {
    [OrganizationType.RECYCLER]: "Recycler",
    [OrganizationType.TRUCK]: "Waste Mover",
    [OrganizationType.WASTE_OUTLET]: "Waste Outlet",
    [OrganizationType.CHARITY]: "Charity",
    [OrganizationType.DISPOSAL]: "Disposal (Landfill)",
};

export function getOrganizationTypeLabel(type: OrganizationType): string {
    return ORGANIZATION_TYPE_LABELS[type] ?? type;
}

export enum IndustryOption {
    // Recycler industries
    PAPER = 'Paper',
    PLASTIC = 'Plastic',
    METAL = 'Metal',
    E_WASTE = 'E-waste',
    GLASS = 'Glass',
    ORGANIC = 'Organic',
    OIL = 'Oil',
    OTHER_RECYCLER = 'Other',
    
    // Truck industries
    GENERAL_HAULING = 'General Hauling',
    RECYCLING_TRANSPORT = 'Recycling Transport',
    HAZARDOUS_WASTE = 'Hazardous Waste',
    BULK_WASTE = 'Bulk Waste',
    OTHER_TRUCK = 'Other',
    
    // Waste Outlet industries
    LANDFILL = 'Landfill',
    COMPOST = 'Compost',
    INCINERATOR = 'Incinerator',
    TRANSFER_STATION = 'Transfer Station',
    OTHER_WASTE_OUTLET = 'Other',

    // Charity industries
    FOOD_BANK = 'Food Bank',
    CLOTHING_DONATION = 'Clothing Donation',
    GENERAL_CHARITY = 'General Charity',
    OTHER_CHARITY = 'Other',

    // Disposal industries
    DISPOSAL_LANDFILL = 'Landfill',
    INCINERATION = 'Incineration',
    CHEMICAL_TREATMENT = 'Chemical Treatment',
    OTHER_DISPOSAL = 'Other',
}

export interface OrganizationRequest {
    name: string;
    organization_type: OrganizationType;
    status?: OrganizationStatus;
    google_maps_url: string;
    data: Record<string, any>;
    logo_asset_id?: string | null;
    logo_filename?: string | null;
}

export interface OrganizationSchema {
    id: string;
    name: string;
    organization_type: OrganizationType;
    status: OrganizationStatus;
    is_pilot: boolean;
    google_maps_url: string;
    data: Record<string, any>;
    logo_url: string | null;
    kyc_completion_percent: number;
    kyc_completed_count: number;
    kyc_required_count: number;
    kyc_missing_items: string[];
    created_at: string;
    updated_at: string;
}

export interface UpdateOrganizationRequest {
    name?: string;
    organization_type?: OrganizationType;
    status?: OrganizationStatus;
    google_maps_url?: string;
    data?: Record<string, any>;
    /** Omit = unchanged. null = remove logo. string = set/replace logo. */
    logo_asset_id?: string | null;
    /** Required when logo_asset_id is a non-null string */
    logo_filename?: string | null;
}

export interface UpdateOrganizationPilotRequest {
    is_pilot: boolean;
}

export interface OrganizationPagination {
    organizations: OrganizationSchema[];
    total: number;
    page: number;
    limit: number;
}

export interface OrganizationQueryParams {
    page?: number;
    limit?: number;
    name?: string | null;
    organization_type?: OrganizationType | null;
    status?: OrganizationStatus | null;
    kyc_min?: number | null;
    kyc_max?: number | null;
    missing_doc_type?: string | null;
    city?: string | null;
}

/** Weekday labels stored in org `data.operating_schedule` (aligned with collection site pickup days). */
export const OPERATING_WEEKDAYS = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
] as const;

export type OperatingWeekday = (typeof OPERATING_WEEKDAYS)[number];

export interface OperatingSchedule {
    is_24_hours: boolean;
    /** Local time of day, `HH:mm` (HTML time input). */
    start_time: string;
    end_time: string;
    operating_days: string[];
}

export function defaultOperatingSchedule(): OperatingSchedule {
    return {
        is_24_hours: false,
        start_time: "09:00",
        end_time: "17:00",
        operating_days: [],
    };
}

export function parseOperatingScheduleFromData(
    data: Record<string, unknown> | undefined
): OperatingSchedule {
    const raw = data?.operating_schedule;
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
        return defaultOperatingSchedule();
    }
    const o = raw as Record<string, unknown>;
    const days = Array.isArray(o.operating_days)
        ? o.operating_days.filter(
              (d): d is string =>
                  typeof d === "string" &&
                  (OPERATING_WEEKDAYS as readonly string[]).includes(d)
          )
        : [];
    return {
        is_24_hours: Boolean(o.is_24_hours),
        start_time:
            typeof o.start_time === "string" && o.start_time
                ? o.start_time
                : "09:00",
        end_time:
            typeof o.end_time === "string" && o.end_time ? o.end_time : "17:00",
        operating_days: days,
    };
}

/** How much of this organization's own work references a waste type. */
export interface OrganizationWasteTypeUsage {
    pickup_orders: number;
    containers: number;
    offers: number;
}

/** A waste type the organization handles -- one row of its basket. */
export interface OrganizationWasteType {
    material_id: string;
    /** The material_types alias that orders, containers and offers reference. */
    material_type_id?: string | null;
    name: string;
    code?: string | null;
    parent_category?: string | null;
    category_code?: string | null;
    status: string;
    client_custom_id?: string | null;
    /** Present only when the list was requested with usage counts. */
    usage?: OrganizationWasteTypeUsage | null;
}
